'use strict';

var _ = require('lodash');
var DataRepresentationService = function DataRepresentationService($filter, currentStateCriteriaService, featureToggleService) {
  var rmNumberFormat = $filter('rmNumberFormat');
  var dataRepresentationToYAxisFormatterMapping = {
    percentage: 'PERCENTAGE_SHORT',
    absolute: 'NUMBER_SHORT'
  };
  var metricKeys = ['m1', 'm2', 'm3', 'm4'];
  function getYAxisFormat(dataRepresentation) {
    return dataRepresentationToYAxisFormatterMapping[dataRepresentation] || dataRepresentationToYAxisFormatterMapping.absolute;
  }
  function getCircles(item) {
    var circles = [];
    if (item.m3 !== undefined) {
      circles.push({
        value: item.m3,
        css: 'm3-circle'
      });
    }
    if (item.m4 !== undefined) {
      circles.push({
        value: item.m4,
        css: 'm4-circle'
      });
    }
    return circles;
  }
  function roundValues(item) {
    _.forOwn(item, function (value, key) {
      if (metricKeys.indexOf(key) > -1) {
        item[key] = value ? +value.toFixed(2) : value;
      }
    });
    return item;
  }
  function calculatePercentageValue(x1, x2) {
    if (_.isNumber(x1) && _.isNumber(x2)) {
      return x2 !== 0 ? (x1 / x2 - 1) * 100 : 0;
    } else {
      return undefined;
    }
  }
  function calculateDiffValue(x1, x2) {
    if (_.isNumber(x1) && _.isNumber(x2)) {
      return x1 - x2;
    } else {
      return undefined;
    }
  }

  /**
   * Prepare chart bar metrics based on data representation
   * @param {Object} item
   * @param {String} dataRepresentation
   * @returns {Object} item
   */
  function prepareChartBarMetrics(item, dataRepresentation) {
    item = roundValues(item);
    switch (dataRepresentation) {
      case 'absolute':
        item.tiles = item.m1 > item.m2 ? [{
          m2: item.m2
        }] : [];
        break;
      case 'percentage':
        item.tiles = [];
        item.absoluteValues = _.pick(item, metricKeys);
        if (currentStateCriteriaService.isPostDeparture()) {
          item.m2 = calculatePercentageValue(item.m1, item.m2);
          item.m4 = undefined;
        } else if (item.isLastYearData) {
          item.m2 = undefined;
          item.m4 = undefined;
        } else {
          item.m2 = calculatePercentageValue(item.m2, item.m4);
          item.m4 = calculatePercentageValue(item.m1, item.m3);
        }
        item.m1 = undefined;
        item.m3 = undefined;
        break;
    }
    item.noData = allUndefined(item);
    item.circles = getCircles(item);
    return item;
  }
  function prepareLFChartBarMetricsInAbsolute(item, dataRepresentation) {
    item = roundValues(item);
    switch (dataRepresentation) {
      case 'absolute':
        item.tiles = item.m1 > item.m2 ? [{
          m2: item.m2
        }] : [];
        break;
      case 'percentage':
        item.tiles = [];
        item.absoluteValues = _.pick(item, metricKeys);
        if (currentStateCriteriaService.isPostDeparture()) {
          item.m2 = calculateDiffValue(item.m1, item.m2);
          item.m4 = undefined;
        } else if (item.isLastYearData) {
          item.m2 = undefined;
          item.m4 = undefined;
        } else {
          item.m2 = calculateDiffValue(item.m2, item.m4);
          item.m4 = calculateDiffValue(item.m1, item.m3);
        }
        item.m1 = undefined;
        item.m3 = undefined;
        break;
    }
    item.noData = allUndefined(item);
    item.circles = getCircles(item);
    return item;
  }
  function allUndefined(item) {
    return _.isUndefined(item.m1) && _.isUndefined(item.m2) && _.isUndefined(item.m3) && _.isUndefined(item.m4);
  }
  function formatValue(n, format, f) {
    f = f || _.identity;
    if (_.isFinite(n)) {
      return rmNumberFormat(f(n), format);
    } else {
      return 'N/A';
    }
  }
  function getReferenceLabels(seasonAvgLabel) {
    return {
      MARKET_SCREEN: {
        lastYear: {
          Curr: 'Curr LY',
          Final: 'Final LY'
        },
        seasonalAverage: {
          Curr: 'Curr Demand Season Avg',
          Final: 'Final Demand Season Avg'
        }
      },
      FLIGHT_SCREEN: {
        lastYear: {
          Curr: 'Curr LY',
          Final: 'Final LY'
        },
        referenceYear: {
          Curr: 'Curr RY',
          Final: 'Final RY'
        },
        seasonalAverage: {
          Curr: 'Curr ' + seasonAvgLabel,
          Final: 'Final ' + seasonAvgLabel
        }
      }
    };
  }
  function getReferenceLabel(selectedReference, screen) {
    var seasonAvgLabel = featureToggleService.isEnabled('REVENUE_OPTIMIZER') ? 'Overbooking Season Avg' : 'Season Avg';
    return getReferenceLabels(seasonAvgLabel)[screen][selectedReference] || getReferenceLabels(seasonAvgLabel)[screen].lastYear;
  }
  function correctLabelForReference(metric, selectedReference, screen) {
    if (metric.dataKey.indexOf('Reference') === -1) {
      return metric.label;
    }
    if (_.isNil(selectedReference)) {
      return metric.label;
    }
    var newLabel = getReferenceLabel(selectedReference, screen)[metric.label];
    return newLabel || metric.label;
  }

  /**
   * Prepares tooltip model that will be rendered for chart bar data
   * @param {Array} metrics
   * @param {Array} data
   * @param {Object} options
   * @param {Object} screen
   * @returns {Object} model
   */
  function prepareTooltipModel(metrics, data, options, screen) {
    var emptyModel = {
      m1: undefined,
      m2: undefined,
      m3: undefined,
      m4: undefined,
      label: undefined,
      percentageValues: {}
    };
    var model = _.defaultsDeep(emptyModel, data, options);
    metrics.forEach(function (metric) {
      model[metric.mappedKey + 'Label'] = correctLabelForReference(metric, model.selectedReference, screen);
      switch (model.representation) {
        case 'absolute':
          model[metric.mappedKey] = formatValue(data[metric.mappedKey], 'NUMBER');
          break;
        case 'percentage':
          model.absoluteValues[metric.mappedKey] = formatValue(data.absoluteValues[metric.mappedKey], 'NUMBER');
          model.percentageValues[metric.mappedKey] = formatValue(data[metric.mappedKey], 'PERCENTAGE', Math.abs);
          break;
      }
    });
    return model;
  }
  return {
    getYAxisFormat: getYAxisFormat,
    prepareTooltipModel: prepareTooltipModel,
    prepareChartBarMetrics: prepareChartBarMetrics,
    prepareLFChartBarMetricsInAbsolute: prepareLFChartBarMetricsInAbsolute,
    correctLabelForReference: correctLabelForReference
  };
};
DataRepresentationService.$inject = ['$filter', 'CurrentStateCriteriaService', 'FeatureToggleService'];
module.exports = DataRepresentationService;